// bars.js — gestapeltes Balkendiagramm als SVG (an die Farbtokens gebunden) + versteckte Datentabelle.
// stackLayout() ist rein (testbar); stackedBars() liefert sicheres Markup für h.js.
import { html, unsafeHtml, escHtml, attrs } from '../core/h.js';

/** „Schöne“ Obergrenze der Y-Achse (1, 2, 5 × 10ⁿ), mindestens 1. */
export function niceMax(v) {
  const n = Math.max(1, Number(v) || 0);
  const p = 10 ** Math.floor(Math.log10(n));
  for (const m of [1, 2, 5, 10]) if (n <= m * p) return m * p;
  return 10 * p;
}

/**
 * Rechtecke für gestapelte Balken.
 * rows: [{…, key1: n, key2: n}], keys: ['key1', …] (unten → oben)
 * → { max, bars: [{ x, w, segs: [{ key, y, h, value }] , total }] }  (Koordinaten in einem w×h-Feld)
 */
export function stackLayout(rows, keys, { w = 600, h = 160, gap = 0.25 } = {}) {
  const totals = rows.map((r) => keys.reduce((s, k) => s + (Number(r[k]) || 0), 0));
  const max = niceMax(Math.max(0, ...totals));
  const slot = rows.length ? w / rows.length : w;
  const bw = Math.max(1, slot * (1 - gap));
  const bars = rows.map((r, i) => {
    let y = h;
    const segs = [];
    for (const k of keys) {
      const value = Number(r[k]) || 0;
      if (!value) continue;
      const sh = (value / max) * h;
      y -= sh;
      segs.push({ key: k, y, h: sh, value });
    }
    return { x: i * slot + (slot - bw) / 2, w: bw, segs, total: totals[i] };
  });
  return { max, bars };
}

/** X-Beschriftung: bis 12 Balken alle, sonst höchstens 8 — vom letzten Balken (= jetzt) aus gezählt, damit sich nichts überlappt. */
export function defaultTick(i, n) {
  if (n <= 12) return true;
  return (n - 1 - i) % Math.ceil(n / 8) === 0;
}

/**
 * series: [{ key, label, tone: 'primary'|'tertiary'|'secondary'|'outline'|'variant' }]
 * labelFor(row) → Achsen-/Tabellenbeschriftung; tick(i, n) → ob die X-Beschriftung gezeigt wird.
 */
export function stackedBars({ rows, series, labelFor, ariaLabel, caption = '', tick }) {
  const W = 600, H = 160;
  const keys = series.map((s) => s.key);
  const { max, bars } = stackLayout(rows, keys, { w: W, h: H });
  const tone = Object.fromEntries(series.map((s) => [s.key, s.tone || 'primary']));
  const every = tick || defaultTick;
  const rects = bars.map((b, i) => b.segs.map((sg) => `<rect class="vc-bars-${tone[sg.key]}" x="${b.x.toFixed(1)}" y="${sg.y.toFixed(1)}" width="${b.w.toFixed(1)}" height="${Math.max(0.5, sg.h).toFixed(1)}" rx="2"><title>${escHtml(labelFor(rows[i]))}: ${escHtml(series.find((s) => s.key === sg.key).label)} ${sg.value}</title></rect>`).join('')).join('');
  const grid = [0.5, 1].map((f) => `<line class="vc-bars-grid" x1="0" x2="${W}" y1="${(H - H * f).toFixed(1)}" y2="${(H - H * f).toFixed(1)}"/>`).join('');
  return html`<figure class="vc-bars" role="group" aria-label="${ariaLabel}">
    <div class="vc-bars-plot">
      <span class="vc-bars-ymax t-label-s t-muted" aria-hidden="true">${max}</span>
      ${unsafeHtml(`<svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" aria-hidden="true" focusable="false">${grid}<line class="vc-bars-axis" x1="0" x2="${W}" y1="${H}" y2="${H}"/>${rects}</svg>`)}
    </div>
    <div class="vc-bars-x t-label-s t-muted" aria-hidden="true">${rows.map((r, i) => html`<span ${attrs({ class: (rows.length - 1 - i) % 2 ? 'is-minor' : null })}>${every(i, rows.length) ? labelFor(r) : ''}</span>`)}</div>
    <figcaption class="vc-bars-legend t-label-m">${series.map((s) => html`<span><i class="vc-bars-dot vc-bars-${s.tone || 'primary'}"></i>${s.label}</span>`)}${caption ? html`<span class="t-muted">${caption}</span>` : ''}</figcaption>
    <div class="vc-visually-hidden"><table><caption>${ariaLabel}</caption>
      <thead><tr><th scope="col"></th>${series.map((s) => html`<th scope="col">${s.label}</th>`)}</tr></thead>
      <tbody>${rows.map((r) => html`<tr><th scope="row">${labelFor(r)}</th>${series.map((s) => html`<td>${Number(r[s.key]) || 0}</td>`)}</tr>`)}</tbody>
    </table></div>
  </figure>`;
}
